import React from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import ProtectedRoute from './ProtectedRoute'
import SettingsRoute from './settingsRoute'
import Page404 from 'pages/page404'
import Profile from 'pages/profile'
import CarriersRoutes from './carriersRoute'
import Reporting from 'pages/reporting'
import AccountsRoutes from './accountsRoute'
import { isAgentLogin, isCarrierLogin, isClaimsOnly } from 'tools/auth'
import { isShortcodeEnabledForNBFleetDashboard } from 'tools/helper'
import strings from 'strings'
import CarrierDetailsNB from 'pages/carriers/partial/carrierDetails/carrierDetailsNB'
import { IMenuAccess } from 'interfaces'
import FleetReporting from 'pages/connectedAccounts/partial/fleetReporting'
import RiskDashboard from 'pages/riskDashboard'
import { RouteComponentProps } from '@reach/router'
import ClaimsSearching from 'pages/claimsSearching'
import FleetMonitoring from 'pages/fleetMonitoring'
import FleetFile from 'pages/fleetFile'
import SSORedirect from 'pages/sso'
import { Loader } from 'components'
import { useFeatureFlag, useIsReportingReady } from 'contexts/ReportingContext'
import { ENTITLEMENT_FLAGS } from 'constants/reporting'

// strings.ROUTES.* carry a trailing '/*' — they are match patterns, not navigable paths.
const EVENTS_FEED_PATH = strings.ROUTES.EVENTS_FEED.replace('/*', '')

interface IApplicationRoute extends RouteComponentProps {
  isAuth: boolean,
  menuAccess: any
}

const ApplicationRoute: React.FunctionComponent<IApplicationRoute> = (
  props
) => {
  const { isAuth, menuAccess } = props
  const flagsReady = useIsReportingReady()
  const fleetFileEnabled = useFeatureFlag(ENTITLEMENT_FLAGS.FLEET_FILE_UI)

  const isAvailableMenuAccess = (path: string) : boolean => {
    return menuAccess.some((menuItem : IMenuAccess) => menuItem.url === path)
  }

  const getCommonRoutes = () => {
    return (
      <>
        <Route path={strings.ROUTES.LOGIN} 
          element={<Navigate to={strings.ROUTES.SETTINGS} />}
        />
        <Route path={`${strings.ROUTES.LOGIN_SSO}/:token/:userId`} 
          element={<SSORedirect />} 
        />
        <Route
          path={strings.ROUTES.PROFILE}
          element={
            <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.PROFILE)}>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Page404 />} />
      </>
    )
  }

  const getCompanyRoutes = () => {
    if (isCarrierLogin()) {
      const useNBFleetDashboard = isShortcodeEnabledForNBFleetDashboard()
      return (
        <>
        <Route path="" element={<Navigate to={strings.ROUTES.FLEET_REPORTING} />} />
        <Route
          path={strings.ROUTES.ACCOUNTS}
          element={
            <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.ACCOUNTS)}>
              <AccountsRoutes />
            </ProtectedRoute>
          }
        />
        {useNBFleetDashboard ? (
          <Route
            path={strings.ROUTES.FLEET_REPORTING}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.FLEET_REPORTING)}>
                <CarrierDetailsNB />
              </ProtectedRoute>
            }
          />
        ) : (
          <Route
            path={strings.ROUTES.FLEET_REPORTING}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.FLEET_REPORTING)}>
                <FleetReporting />
              </ProtectedRoute>
            }
          />
        )}
        {useNBFleetDashboard && (
          <Route
            path={strings.ROUTES.EVENTS_FEED}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.EVENTS_FEED)}>
                <ClaimsSearching carrierView useKilometers={useNBFleetDashboard} />
              </ProtectedRoute>
            }
          />
        )}
        </>
      )
    }
    if (isAgentLogin()) {
      return (
        <>
        <Route path="" element={<Navigate to="/dashboard" />} />
        <Route
            path={strings.ROUTES.DASHBOARD}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.DASHBOARD)}>
                <RiskDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.CARRIERS}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.CARRIERS)}>
                <CarriersRoutes />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.FLEET_REPORTING}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.FLEET_REPORTING)}>
                <FleetReporting />
              </ProtectedRoute>
            }
          />
        </>
      )
    }
    else {
      return (
        <>
          {/* CLAIMS_ONLY has no Dashboard entry, so /dashboard would 404 right after login. The
              SSO path has its own hardcoded landing nav and needs the same redirect. */}
          <Route
            path=""
            element={<Navigate to={isClaimsOnly() ? EVENTS_FEED_PATH : '/dashboard'} />}
          />
          <Route
            path={strings.ROUTES.DASHBOARD}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.DASHBOARD)}>
                <RiskDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.SETTINGS}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.SETTINGS)}>
                <SettingsRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.CARRIERS}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.CARRIERS)}>
                <CarriersRoutes />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.REPORTING}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.REPORTING)}>
                <Reporting />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.EVENTS_FEED}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.EVENTS_FEED)}>
                <ClaimsSearching useKilometers={isShortcodeEnabledForNBFleetDashboard()} />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.FLEET_MONITORING}
            element={
              <ProtectedRoute isAuth={isAuth} hasAccess={isAvailableMenuAccess(strings.ROUTES.FLEET_MONITORING)}>
                <FleetMonitoring />
              </ProtectedRoute>
            }
          />
          <Route
            path={strings.ROUTES.FLEETFILE}
            element={
              flagsReady ? (
                <ProtectedRoute
                  isAuth={isAuth}
                  hasAccess={fleetFileEnabled && isAvailableMenuAccess(strings.ROUTES.FLEETFILE)}
                >
                  <FleetFile />
                </ProtectedRoute>
              ) : (
                <Loader />
              )
            }
          />
        </>
      )
    }
  }

  return (
    <Routes>
      {getCommonRoutes()}
      {getCompanyRoutes()}
    </Routes>
  )
}

export default ApplicationRoute
